// FIXME: MAGGIE-3904
import { css } from '@emotion/react';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';

import { FormControl, FormHelperText, InputBase, Stack, useTheme } from '@mui/material';
import { useAtomValue } from 'jotai/index';

import { EditorState } from 'lexical';
import { useCallback, useMemo, useState } from 'react';
import { Controller, ControllerRenderProps, FormProvider, useForm } from 'react-hook-form';
import { RendererType } from '../../data-models/field-definition';
import { ISimpleChoice } from '../../data-models/field2.data-model';
import { createNoteAttendeeDataModel } from '../../data-models/note-comment.data-model';
import {
  createDraftNoteModel,
  ILexicalNoteFormValues,
  INoteDataModel,
} from '../../data-models/note.data-model';
import { requiredMsg } from '../../pages/Finance/components/TransactionModal/Forms/utils/validationMessages';
import { PermissionKey } from '../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../services/PermissionService';
import { usersByIdMapAtom } from '../../services/state/AppConfigStateJ';
import { captureUnexpectedState } from '../../util/SentryUtils';
import { createFormField, IFormFieldSelectMeta } from '../../view-models/form.view-model';
import { FieldFactory } from '../Form/FieldFactory';
import { LexicalFooter } from './components/LexicalFooter';
import { LexicalHeader } from './components/LexicalHeader';
import './editorStyles.css';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import { AutoSavePlugin } from './plugins/AutoSavePlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin/FloatingTextFormatToolbarPlugin';
import ToolbarPlugin from './plugins/ToolbarPlugin';
import { useLexicalContext } from './provider/LexicalProvider';
import DefaultTheme from './themes/DefaultTheme';

const editorMainContainerCss = css`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const dividerLineCss = css`
  background-color: var(--mui-colors-neutral-30);
  height: 1px;
  border-radius: 10px;
`;

const noteContainerCss = css`
  display: grid;
  grid-template-rows: min-content min-content 1fr;
  overflow: hidden;
  padding: 1rem 1.5rem;
  width: 100%;
`;

export function getLexicalEditorBaseConfig(): Pick<InitialConfigType, 'theme' | 'onError' | 'nodes'> {
  return {
    // The editor theme
    theme: DefaultTheme,
    // Handling of errors during update
    onError(error: Error) {
      throw error;
    },
    // Any custom nodes go here
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
    ],
  };
}

function Placeholder() {
  return <div className='editor-placeholder'>Type your note here</div>;
}

const draftNote = JSON.stringify(createDraftNoteModel());

export function LexicalEditor() {
  const values = useLexicalContext();
  const { noteData, setNoteData, onExit, editorStateRef } = values;

  const { colors } = useTheme();
  const userMap = useAtomValue(usersByIdMapAtom);
  const hasAuditorAccess = PermissionService.get().hasPermission(PermissionKey.hasAuditorAccess);
  const userOptions: ISimpleChoice<number>[] = useMemo(
    () =>
      [...userMap.values()].map((user) => ({
        displayName: user.name,
        value: user.id,
        disabled: user.activeStatus !== true,
      })),
    [userMap]
  );

  const methods = useForm<ILexicalNoteFormValues>({
    mode: 'onChange',
    defaultValues: {
      title: noteData?.title ?? '',
      body: {
        attendees: getInitialAttendees(noteData),
      },
    },
  });

  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  const onChangeEditorState = useCallback(
    (editorState: EditorState) => {
      editorStateRef.current = editorState;
      setNoteData({
        ...noteData!,
        body: {
          ...noteData!.body,
          data: editorState.toJSON(),
        },
      });
    },
    [editorStateRef, noteData, setNoteData]
  );

  const attendeesField = useMemo(() => {
    return createFormField<IFormFieldSelectMeta<number>>({
      key: 'localAttendees',
      label: 'Attendees',
      required: false,
      renderer: RendererType.multiSelect,
      rendererMeta: {
        values: userOptions,
        multi: true,
      },
      fullWidth: true,
      disabled: hasAuditorAccess,
    });
  }, [userOptions, hasAuditorAccess]);
  // FieldMultiSelect expects option values to be a primitive, so we need to keep local state and transform it to the form value
  const [attendees, setAttendees] = useState<number[]>(() => {
    return getInitialAttendees(noteData).map((attendee) => attendee.userId);
  });
  const { setValue } = methods;
  const attendeesFieldFormProps = useMemo(() => {
    return {
      onChange: (newValue: number[]) => {
        setAttendees(newValue);
        setValue(
          'body.attendees',
          newValue.map((id) =>
            createNoteAttendeeDataModel({
              userId: id,
            })
          ),
          { shouldDirty: true, shouldValidate: true }
        );
      },
      value: attendees,
    };
  }, [setValue, attendees]);

  return (
    <div css={editorMainContainerCss}>
      <FormProvider {...methods}>
        <LexicalHeader onExit={onExit} />

        <div css={dividerLineCss} />

        <div css={noteContainerCss}>
          <Controller
            name='title'
            rules={{ required: true }}
            render={({ field: { ref, ...titleField } }) => {
              const hasError = !!methods.formState.errors.title;
              return (
                <FormControl error={hasError} variant='standard' fullWidth>
                  <InputBase
                    {...titleField}
                    aria-describedby='note-title-error-text'
                    placeholder='Note Title'
                    sx={{ fontSize: '10px', marginTop: 0, width: '100%' }}
                    inputRef={ref}
                    disabled={hasAuditorAccess}
                    inputProps={{
                      sx: {
                        fontSize: '24px',
                        height: '50px',
                        backgroundColor: 'white',
                        pt: 0,
                        border: 0,
                        '&::placeholder': {
                          color: colors.neutral[70],
                        },
                      },
                      autoFocus: !hasAuditorAccess,
                    }}
                  />
                  {hasError && (
                    <FormHelperText id='note-title-error-text'>{requiredMsg('Title')}</FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />

          <Stack
            gap='.75rem'
            display={'grid'}
            gridTemplateColumns={'auto 1fr'}
            justifyContent={'center'}
            alignItems={'center'}
          >
            <AccountCircleOutlinedIcon sx={{ color: colors.neutral[50], fontSize: '1.5rem' }} />
            <FieldFactory
              formField={attendeesField}
              formProps={attendeesFieldFormProps as ControllerRenderProps}
            />
          </Stack>

          <LexicalComposer
            initialConfig={{
              ...getLexicalEditorBaseConfig(),
              namespace: 'Company Note',
              editorState: JSON.stringify(noteData!.body.data) ?? draftNote,
              editable: !hasAuditorAccess,
            }}
          >
            <div className='editor-container'>
              <AutoSavePlugin />
              {!hasAuditorAccess && <ToolbarPlugin />}
              <div className='editor-inner'>
                <RichTextPlugin
                  contentEditable={
                    <div ref={onRef}>
                      <ContentEditable className='editor-input' />
                    </div>
                  }
                  placeholder={<Placeholder />}
                  ErrorBoundary={LexicalErrorBoundary}
                />
                <OnChangePlugin onChange={onChangeEditorState} />
                <HistoryPlugin />
                <FloatingTextFormatToolbarPlugin anchorElem={floatingAnchorElem as HTMLElement} />
                <AutoLinkPlugin />
                <ListPlugin />
                <LinkPlugin />
              </div>
            </div>
          </LexicalComposer>
        </div>
        {!hasAuditorAccess && <LexicalFooter />}
      </FormProvider>
    </div>
  );
}

export function getInitialAttendees(noteData?: INoteDataModel<unknown> | null) {
  // the check is for backwards compatibility with old notes that have bad data
  if (Array.isArray(noteData?.body?.attendees)) {
    return noteData.body.attendees;
  }
  captureUnexpectedState('getInitialAttendees', { noteData });
  return [];
}
